import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DateTime } from 'luxon';
import { useReducedMotion } from '@hooks/useReducedMotion';
import { PanelCard } from '../../shared/PanelCard/PanelCard';
import { StatBlock } from './StatBlock';
import classes from './WorkersHoursChart.module.css';
import type { IDailyWorkersHours } from '../types';

/* Module-level constants — hoisted from inside the render body so the JSX
   prop references are stable across renders. Recharts compares chart-element
   props by reference; new objects each render would defeat the internal
   memoization on axis / bar / line layout work. The inline `fontSize` is a
   deliberate departure from `--fs-xs` (12px) — Recharts can't read CSS
   custom properties from JS, so the value is fixed here. */
const CHART_MARGIN = { top: 8, right: 16, left: 0, bottom: 4 } as const;
const AXIS_TICK_GRAY = { fill: 'var(--g500)', fontSize: 11 } as const;
const AXIS_TICK_CYAN = { fill: 'var(--chart-cyan)', fontSize: 11 } as const;
const AXIS_TICK_PURPLE = { fill: 'var(--line-purple)', fontSize: 11 } as const;
const TOOLTIP_CURSOR = { fill: 'var(--chart-cyan-tint)' } as const;
const BAR_RADIUS: [number, number, number, number] = [2, 2, 0, 0];
const TRANS_TITLE_COMPONENTS = [
  <span className={classes.titleBars} key="bars" />,
  <span className={classes.titleLine} key="line" />,
];

interface IWorkersHoursChartProps {
  series: IDailyWorkersHours[];
  avgWorkers: number;
  avgHours: number;
  /** Project timezone — drives the x-axis date formatting so labels match
      the calendar day the wire YYYY-MM-DD string represents in `tz`. */
  tz: string;
}

/**
 * Workers-per-day (bars, left axis) vs Hours-per-day (line, right axis) for
 * the selected range. Mirrors the source page's combo chart exactly: light
 * blue bars (`--bar-blue`) anchored to the left axis 0..max+1 scale, dark
 * purple line (`--line-purple`) anchored to the right axis 0..ceiling-to-20
 * scale, x-axis labels short-formatted ("May 18").
 */
export function WorkersHoursChart({ series, avgWorkers, avgHours, tz }: IWorkersHoursChartProps) {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();
  const animationDuration = prefersReducedMotion ? 0 : 800;
  const lineAnimationDuration = prefersReducedMotion ? 0 : 900;

  // Project the series rows + compute axis ceilings in a single memo so the
  // Recharts `data` / `domain` props keep stable references across unrelated
  // page re-renders. Tight axis ceilings (workers = exact data max, hours =
  // next multiple of 6) match trusstor's V2 chart: 0/6/12/18/24 tick set for
  // sub-day hour ranges, no top padding on workers.
  const { data, maxWorkers, hoursMax } = useMemo(() => {
    const rows = series.map((row) => ({
      label: DateTime.fromISO(row.date, { zone: tz }).toFormat('LLL d'),
      // Carry the original YYYY-MM-DD into the row so the tooltip can
      // reformat it as `DD.MM.YYYY` (the trusstor V2 header format) instead
      // of the abbreviated x-axis label.
      isoDate: row.date,
      workers: row.workers,
      hours: row.hours,
    }));
    let workersMax = 1;
    let hoursMaxRaw = 6;
    for (const row of series) {
      if (row.workers > workersMax) workersMax = row.workers;
      if (row.hours > hoursMaxRaw) hoursMaxRaw = row.hours;
    }
    return {
      data: rows,
      maxWorkers: workersMax,
      hoursMax: Math.ceil(hoursMaxRaw / 6) * 6,
    };
  }, [series, tz]);

  return (
    <PanelCard aria-label={t('reports.contractorAnalysis.personnel.title')} className={classes.panelPinned}>
      <h3 className={classes.title}>
        <Trans
          i18nKey="reports.contractorAnalysis.personnelChart.title"
          components={TRANS_TITLE_COMPONENTS}
        />
      </h3>

      <div className={classes.averages}>
        <StatBlock
          icon="helmet"
          label={t('reports.contractorAnalysis.personnelChart.avgWorkers')}
          value={avgWorkers}
        />
        <StatBlock
          icon="clock"
          label={t('reports.contractorAnalysis.personnelChart.avgHours')}
          value={avgHours}
        />
      </div>

      <div className={classes.chart} data-chart-no-kb-nav>
        <ResponsiveContainer width="100%" height="100%" minHeight={220}>
          <ComposedChart
            data={data}
            margin={CHART_MARGIN}
            // Recharts' accessibility layer adds a focusable wrapper +
            // tab-stops on each bar/line so keyboard users can navigate
            // the series. Side effect: clicking a bar focuses that layer
            // and the browser paints its default outline around the plot
            // area. We don't expose keyboard navigation on this chart
            // (it's a static summary), so disabling the layer is both
            // the correct accessibility call AND removes the outline
            // without any CSS workaround.
            accessibilityLayer={false}
          >
            {/* Horizontal grid lines anchored to the left ("workers") axis
                ticks. Recharts' CartesianGrid only auto-renders lines at
                the *first* yAxis's ticks; with two y-axes and the workers
                one not being default it would otherwise draw a single
                line at the top. Naming the axis explicitly + passing the
                exact tick set as `horizontalPoints` would also work, but
                the supported way is the `yAxisId` prop. */}
            <CartesianGrid
              yAxisId="workers"
              stroke="var(--g200)"
              strokeDasharray="3 3"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={AXIS_TICK_GRAY}
              axisLine={{ stroke: 'var(--g200)' }}
              tickLine={false}
              interval={0}
            />
            <YAxis
              yAxisId="workers"
              tick={AXIS_TICK_CYAN}
              axisLine={false}
              tickLine={false}
              domain={[0, maxWorkers]}
              allowDecimals
            />
            <YAxis
              yAxisId="hours"
              orientation="right"
              tick={AXIS_TICK_PURPLE}
              axisLine={false}
              tickLine={false}
              domain={[0, hoursMax]}
              allowDecimals={false}
            />
            <Tooltip cursor={TOOLTIP_CURSOR} content={TOOLTIP_CONTENT} />
            <Bar
              yAxisId="workers"
              dataKey="workers"
              name={t('reports.contractorAnalysis.personnelChart.legendWorkers')}
              fill="var(--chart-cyan)"
              barSize={40}
              radius={BAR_RADIUS}
              animationDuration={animationDuration}
              animationEasing="ease-out"
            />
            <Line
              yAxisId="hours"
              type="monotone"
              dataKey="hours"
              name={t('reports.contractorAnalysis.personnelChart.legendHours')}
              stroke="var(--line-purple)"
              strokeWidth={3}
              dot={false}
              animationDuration={lineAnimationDuration}
              animationEasing="ease-out"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className={classes.legend}>
        <span className={classes.legendItem}>
          <span className={`${classes.legendDot} ${classes.legendDotBar}`} aria-hidden="true" />
          {t('reports.contractorAnalysis.personnelChart.legendWorkers')}
        </span>
        <span className={classes.legendItem}>
          <span className={`${classes.legendDot} ${classes.legendDotLine}`} aria-hidden="true" />
          {t('reports.contractorAnalysis.personnelChart.legendHours')}
        </span>
      </div>
    </PanelCard>
  );
}

/* -------------------------------------------------------------------------- */
/* Custom Recharts tooltip                                                     */
/* -------------------------------------------------------------------------- */

// Stable JSX element reference — Recharts cloneElement-injects the per-hover
// `active`/`payload` props at render time, so a single hoisted instance avoids
// the per-render element churn that `content={<ChartTooltipContent />}` would
// cause inside the chart's render body. Local prop interface is preserved
// because we read `payload[0].payload.isoDate` (a custom field stitched into
// each data row), which recharts' built-in `TooltipProps<TValue, TName>`
// types as `any`.
const TOOLTIP_CONTENT = <ChartTooltipContent />;

interface TooltipPayloadItem {
  value: number;
  name?: string;
  dataKey?: string | number;
  color?: string;
  payload?: { isoDate?: string };
}

interface IChartTooltipContentProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
}

/**
 * Two-section tooltip — dark header strip with the full DD.MM.YYYY date,
 * white body with one colored-dot row per series. Mirrors trusstor's V2
 * chart tooltip exactly.
 *
 * Recharts hands every hovered series in `payload`; the underlying row's
 * full `isoDate` rides through `payload[0].payload.isoDate` (we stitched
 * it into the data above). The dot color comes from each series'
 * `color` (the `fill`/`stroke` Recharts attached when it laid the
 * bar/line out).
 */
function ChartTooltipContent({ active, payload }: IChartTooltipContentProps) {
  if (!active || !payload || payload.length === 0) return null;
  const iso = payload[0]?.payload?.isoDate;
  return (
    <div className={classes.tooltip}>
      <div className={classes.tooltipHeader}>{formatTooltipDate(iso)}</div>
      <div className={classes.tooltipBody}>
        {payload.map((row) => (
          <div key={String(row.dataKey)} className={classes.tooltipRow}>
            <span
              className={classes.tooltipDot}
              style={{ background: row.color }}
              aria-hidden="true"
            />
            <span className={classes.tooltipText}>
              {row.name}: {row.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatTooltipDate(iso: string | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${m[3]}.${m[2]}.${m[1]}`;
}
